import React, { useState, useEffect } from "react";
import Grid from "utils/Grid";
import Box from "utils/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import TablePaginationActions from "components/Common/TableGenerator/TablePaginationActions";
import GetAppIcon from "@mui/icons-material/GetApp";
import { connect, useSelector } from "react-redux";
import moment from "dayjs";
import { useSnackbar } from "notistack";
import { domainLevelConfig } from "ui-config";
import _ from "lodash";
import CustomInputDatePicker from "../../../../../Common/CustomInputDatePicker";
import { ButtonIcon, ApplyButton } from "components/Common/Buttons";
import { downloadBlob } from "helpers";
import AsyncAutoComplete from "../common/AsyncAutoComplete";
import {
  convertUtcToLocal,
  dateFormatter,
  getFilterEndDate,
  getFilterStartDate,
  getTimeZoneDifference,
} from "utils/helpers";
import styles from "./styles";

function Orders(props) {
  const classes = styles();

  const operatorsData = useSelector(
    (state) => state.dropdownReducer.operatorsData
  );
  const areasData = useSelector((state) => state.dropdownReducer.areasData);
  const locationsData = useSelector(
    (state) => state.dropdownReducer.locationsData
  );
  const vmData = useSelector((state) => state.dropdownReducer.devicesData);
  const orderStatusData = useSelector(
    (state) => state.dropdownReducer.orderStatusData
  );
  const productsData = useSelector(
    (state) => state.dropdownReducer.productsData
  );

  const [startDate, setStartDate] = useState(
    moment().subtract(15, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [locationList, setLocationList] = useState(locationsData || []);
  const [areaList, setAreaList] = useState(areasData || []);
  const [vmList, setVmList] = useState(vmData || []);
  const host = useSelector((state) => state.userReducer.host);
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loader, setLoader] = useState(false);
  const [exportingPDFLoader, setExportingPDFLoader] = useState(false);
  const [exportingExcelLoader, setExportingExcelLoader] = useState(false);
  const [fetchDataParams, setFetchDataParams] = useState({});
  const [filters, setFilters] = useState({
    operator: { value: "all", label: "All" },
    area: { value: "all", label: "All" },
    location: { value: "all", label: "All" },
    vm: { value: "all", label: "All" },
    product: { value: "all", label: "All" },
    status: { value: "all", label: "All" },
  });
  const [areaListLoading, setAreaListLoading] = useState(false);
  const [locationListLoading, setLocationListLoading] = useState(false);
  const [vmListLoading, setVmListLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchLocations = async (areaList = [], area_id, selectedOperator) => {
    try {
      setLocationListLoading(true);

      if (area_id) {
        const list =
          _.find(areaList, (x) => x.value === area_id)?.locations || [];

        setLocationList([
          { label: "All", value: "all" },
          ..._.map(list, ({ location_id, location_name }) => ({
            label: location_name,
            value: location_id,
          })),
        ]);
      } else {
        if (selectedOperator && selectedOperator?.value !== "all") {
          let newLocation = [];
          const locationData = [
            ..._.map(areaList, ({ locations }) =>
              Array.prototype.push.apply(newLocation, locations)
            ),
          ];
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(newLocation, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        } else {
          const { data } = await window.axiosIns("locations/locations", {
            params: {
              all: true,
              state: "all",
              ordering: "location_name",
              lite: true,
            },
          });
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(data?.results, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLocationListLoading(false);
    }
  };

  const fetchVM = async (selectedOperator, selectedArea, selectedLocation) => {
    try {
      setVmListLoading(true);
      const params = {
        all: true,
        ordering: "vm_name",
        lite: true,
      };
      if (selectedOperator && selectedOperator.value !== "all")
        params.company_id = selectedOperator.value;
      if (selectedArea && selectedArea.value !== "all")
        params.area_id = selectedArea.value;
      if (selectedLocation && selectedLocation.value !== "all")
        params.location = selectedLocation.value;
      const { data } = await window.axiosIns("device", {
        params,
      });
      setVmList([
        { label: "All", value: "all" },
        ..._.map(data?.data?.results, ({ id, serial_number, vm_name }) => ({
          label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
          value: id,
        })),
      ]);
    } catch (err) {
      console.log(err);
      setVmListLoading(false);
    } finally {
      setVmListLoading(false);
    }
  };

  const fetchAreas = async (selectedOperator) => {
    try {
      setAreaListLoading(true);

      const params = { all: true, ordering: "area_name" };
      if (selectedOperator && selectedOperator.value !== "all")
        params.operator_id = selectedOperator.value;

      const { data } = await window.axiosIns("locations/areas", {
        params,
      });

      setAreaList([
        { label: "All", value: "all" },
        ..._.map(data?.results, ({ area_id, area_name, locations }) => ({
          label: area_name,
          value: area_id,
          locations,
        })),
      ]);
      fetchLocations(areaList, null, selectedOperator);
    } catch (err) {
      console.log(err);
      fetchLocations([], null, selectedOperator);
    } finally {
      setAreaListLoading(false);
    }
  };

  const buildCommonParams = () => {
    const baseParams = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference?.(),
      _scope: "AND",
    };

    const filterMap = {
      vm: ["device_id"],
      location: ["device__location_id"],
      area: ["device__location__area_id"],
      operator: ["device__company_id"],
      product: ["products"],
      status: ["status"],
    };

    Object.entries(filterMap).forEach(([key, [paramKey]]) => {
      const value = filters[key]?.value;
      if (value && value !== "all") {
        baseParams[paramKey] = value;
      }
    });

    return baseParams;
  };

  const fetchExcel = async () => {
    if (count > 35000) {
      enqueueSnackbar(
        "Number of Records can not be more than 35000 for Excel download. Please lower the date range.",
        { autoHideDuration: 4000 }
      );
      return;
    }

    setExportingExcelLoader(true);
    const params = buildCommonParams();
    try {
      const { data } = await window.axiosIns("orders/export_data", {
        responseType: "blob",
        params,
      });
      if (data) {
        downloadBlob(
          new Blob([data], { type: "application/xlsx" }),
          "orders_report.xlsx"
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setExportingExcelLoader(false);
    }
  };

  const fetchPDF = async () => {
    if (count > 15000) {
      enqueueSnackbar(
        "Number of Records can not be more than 15000 for PDF download. Please lower the date range.",
        { autoHideDuration: 4000 }
      );
      return;
    }

    setExportingPDFLoader(true);
    const params = {
      ...buildCommonParams(),
      model: "order",
      type: "pdf",
    };
    try {
      const { data } = await window.axiosIns("orders/export", {
        responseType: "blob",
        params,
      });
      if (data) {
        downloadBlob(new Blob([data]), "order_reports.pdf");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setExportingPDFLoader(false);
    }
  };

  const fetchOrder = async (_page, _limit, _runreport) => {
    if (_runreport) {
      setPage(0);
    }

    setLimit(_limit);
    const params = {
      ...buildCommonParams(),
      page: _runreport ? 1 : _page + 1,
      limit: _limit,
    };
    if (_.isEqual(params, fetchDataParams)) return;
    setFetchDataParams(params);
    setLoader(true);
    try {
      const { data } = await window.axiosIns("orders", { params });
      setCount(data.count);
      setOrders(data.results);
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchOrder(0, 10);
  }, []);

  const handleChangePage = (_, page) => {
    setPage(page);
    fetchOrder(page, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
    fetchOrder(0, parseInt(event.target.value, 10));
  };

  const hostConfig =
    domainLevelConfig[host] || domainLevelConfig["default"] || {};

  const truncate = (str) =>
    str.length > 12 ? str.substr(0, 12 - 1) + "..." : str;

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleOperator = (val) => {
    updateFilter("operator", val);
    fetchAreas(val);
    fetchVM(val);
    updateFilter("area", { value: "all", label: "All" });
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleArea = (val) => {
    updateFilter("area", val);
    fetchLocations(areaList, val.value, filters.operator);
    fetchVM(filters.operator, val);
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleLocation = (val) => {
    updateFilter("location", val);
    fetchVM(filters.operator, filters.area, val);
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <Grid spacing={1} container style={{ marginBottom: "1rem" }}>
        <Grid xs={10}>
          <Grid container spacing={1}>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={startDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => {
                  setStartDate(date);
                  setPage(0);
                }}
                label="Start Date"
                maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={endDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => {
                  setEndDate(date);
                  setPage(0);
                }}
                label="End Date"
                minDate={new Date(startDate)}
                maxDate={new Date()}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleOperator}
                value={filters.operator}
                loading={false}
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
                options={operatorsData || []}
                label="Operator"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleArea}
                loading={areaListLoading}
                value={filters.area}
                options={areaList}
                label="Area"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleLocation}
                value={filters.location}
                loading={locationListLoading}
                options={locationList}
                label="Location"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("vm", val);
                  setPage(0);
                }}
                options={vmList}
                loading={vmListLoading}
                value={filters.vm}
                label="Vending Machine"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("product", val);
                  setPage(0);
                }}
                options={productsData}
                value={filters.product}
                loading={false}
                label="Products"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("status", val);
                  setPage(0);
                }}
                options={orderStatusData}
                value={filters.status}
                loading={false}
                label="Order Status"
                disabled={
                  areaListLoading || locationListLoading || vmListLoading
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={2}>
          <ApplyButton
            size="large"
            fullWidth
            onClick={() => fetchOrder(page, limit, true)}
            style={{ marginBottom: "16px", padding: "15px 0" }}
            disabled={loader || exportingPDFLoader || exportingExcelLoader}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ButtonIcon
              label="Excel"
              fullWidth
              size="large"
              Icon={GetAppIcon}
              onClick={fetchExcel}
              disabled={exportingExcelLoader || orders?.length <= 0}
              style={{ marginRight: "4px", padding: "15px 0" }}
              loading={exportingExcelLoader}
            />
            <ButtonIcon
              label="PDF"
              fullWidth
              size="large"
              Icon={GetAppIcon}
              onClick={fetchPDF}
              disabled={exportingPDFLoader || orders?.length <= 0}
              style={{ padding: "15px 0" }}
              loading={exportingPDFLoader}
            />
          </div>
        </Grid>
      </Grid>
      <Box
        mt={4}
        className="mt-3 pt-4 px-4"
        style={{
          boxShadow: "0 0 10px lightgrey",
          fontSize: "0.8rem",
          margin: "0",
          maxWidth: "100%",
        }}
      >
        <Grid container>
          <Grid xs={3}>
            <div className={classes.navLogoWrapper}>
              <div
                className={classes.navLogo}
                style={{
                  backgroundImage: `url(${
                    props.appLogo
                      ? props.appLogo
                      : hostConfig.navLogo ||
                        props.currentUser.company.logo ||
                        hostConfig.navLogo
                  })`,
                }}
              ></div>
            </div>
          </Grid>
          <Grid xs={6}>
            <Typography align="center" variant="h3">
              Orders Report
            </Typography>
            {startDate &&
              endDate &&
              filters.area.value &&
              filters.location.value &&
              filters.operator?.value &&
              filters.vm.value && (
                <Typography align="center">
                  {`Date Range:${dateFormatter(startDate)} to ${dateFormatter(
                    endDate
                  )}, Operator: ${filters.operator.label}, Area: ${
                    filters.area.label
                  }, Location: ${filters.location.label}, Vending Machine: ${
                    filters.vm.label
                  }, Product: ${filters.product.label}, Status: ${
                    filters.status.label
                  }`}
                </Typography>
              )}
          </Grid>
          <Grid xs={3}>
            <Box justifyContent="flex-end" display="flex">
              <Typography>
                <b>
                  Generated on :{" "}
                  {new Date().toLocaleString("en-us", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </b>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <div
          style={{ height: "auto", overflow: "auto", overflowX: "hidden" }}
          className="hide-scrollbar"
        >
          <div className="table-responsive-sm mt-4">
            <table className="table logs-table w-100">
              <thead>
                <tr className="border-none">
                  {props.currentUser.type === "SA" &&
                    filters.operator.value === "all" && (
                      <th scope="col" style={{ verticalAlign: "top" }}>
                        Operator
                      </th>
                    )}
                  {props.currentUser.type === "SA" &&
                    filters.location.value === "all" && (
                      <th scope="col" style={{ verticalAlign: "top" }}>
                        Location
                      </th>
                    )}
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Serial Number
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    VM Name
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Order ID
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Transaction ID
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Order Status
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Amount ($)
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    No of Items Ordered
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    No of Items Delivered
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Order Items
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    User
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Placed at
                  </th>
                </tr>
              </thead>
              {!loader && (
                <tbody>
                  {orders.map((res, index) => {
                    return (
                      <tr key={index} className="new-entry">
                        {props.currentUser.type === "SA" &&
                          filters.operator.value === "all" && (
                            <td
                              style={{
                                width: "5%",
                                padding: "0.75rem 0.25rem",
                                wordBreak: "break-all",
                              }}
                            >
                              {res.operator_name ?? ""}
                            </td>
                          )}
                        {props.currentUser.type === "SA" &&
                          filters.location.value === "all" && (
                            <td
                              style={{
                                width: "5%",
                                padding: "0.75rem 0.25rem",
                                wordBreak: "break-all",
                              }}
                            >
                              {res.location_name ?? ""}
                            </td>
                          )}
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.device_serial ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.vm_name ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.order_id ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.reference_transaction_id ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.order_status ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.total_amount ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.total_order ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.total_success ?? ""}
                        </td>
                        <Tooltip
                          placement="top"
                          size="small"
                          title={res.order_data.map((i, __i) => {
                            return (
                              <React.Fragment key={__i}>
                                {i}
                                <br />
                              </React.Fragment>
                            );
                          })}
                          aria-label="add"
                        >
                          <td
                            style={{
                              width: "5%",
                              padding: "0.75rem 0.25rem",
                              wordBreak: "break-all",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: res.order_data
                                .map((i) => truncate(i))
                                .join("<br>"),
                            }}
                          ></td>
                        </Tooltip>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.user ?? ""}
                        </td>
                        <td
                          style={{
                            width: "6%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.order_date
                            ? dateFormatter(
                                convertUtcToLocal(res.order_date),
                                true
                              )
                            : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
            {orders.length === 0 && (
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <strong> No records found</strong>
              </div>
            )}
            {loader && (
              <Box display="flex" justifyContent="center" mt={1}>
                <CircularProgress style={{ height: 35, width: 35 }} />
              </Box>
            )}
          </div>
        </div>
      </Box>

      <TablePagination
        rowsPerPageOptions={[3, 5, 10, 25, 50, 100]}
        count={count}
        rowsPerPage={limit}
        page={page}
        slotProps={{ select: { "aria-label": "rows per page" } }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={(e) => TablePaginationActions(e, props)}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.userReducer.current_user,
    appLogo: state.userReducer.appLogo,
  };
};

export default connect(mapStateToProps)(Orders);
